import React, { useMemo } from 'react';
import { Row, Col, Card, Badge, Spinner } from 'react-bootstrap';
import { useQuery } from '@apollo/client/react';
import { Link } from 'react-router-dom';
import moment from 'moment';

import ProfileLayout from 'components/marketing/instructor/ProfileLayout';
import { GET_MY_EVENT_CERTIFICATES } from 'helper/graphql/queries';
import { PATH_EVENTS } from 'routes/Paths';

const formatTypeLabel = (t) =>
	t ? t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Event';

// Deterministic colour from event type string (matches EventCard palette)
const TYPE_COLOURS = ['#1d9eb0', '#F68521', '#8b5cf6', '#10b981', '#ef4444', '#f59e0b'];
const getTypeColour = (str = '') => {
	let h = 0;
	for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffffffff;
	return TYPE_COLOURS[Math.abs(h) % TYPE_COLOURS.length];
};

const CertificateCard = ({ cert }) => {
	const { event, issuedAt, certificateUrl } = cert;
	const colour = getTypeColour(event?.eventType);
	const isPast = event?.sessionDatetime && moment(event.sessionDatetime).isBefore(moment());

	return (
		<Card className="mc-card h-100">
			{/* Coloured top bar */}
			<div className="mc-card-bar" style={{ background: colour }} />

			<Card.Body className="d-flex flex-column p-4">
				<div className="d-flex align-items-start justify-content-between mb-3">
					<Badge
						className="mc-type-badge"
						style={{ background: `${colour}18`, color: colour }}
					>
						{formatTypeLabel(event?.eventType)}
					</Badge>
					{isPast && (
						<Badge bg="" className="mc-attended-badge">
							<i className="fe fe-check me-1"></i>Attended
						</Badge>
					)}
				</div>

				<h5 className="mc-event-title mb-2">{event?.title}</h5>

				{event?.speakerName && (
					<p className="mc-speaker mb-3">
						<i className="fe fe-mic me-1"></i>
						{event.speakerName}
					</p>
				)}

				<div className="mc-meta mb-4">
					{event?.sessionDatetime && (
						<div className="mc-meta-row">
							<i className="fe fe-calendar me-2"></i>
							{moment(event.sessionDatetime).format('MMMM D, YYYY')}
						</div>
					)}
					{issuedAt && (
						<div className="mc-meta-row">
							<i className="fe fe-award me-2"></i>
							Issued {moment(issuedAt).format('MMMM D, YYYY')}
						</div>
					)}
				</div>

				<div className="mt-auto">
					{certificateUrl ? (
						<a
							href={certificateUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="btn mc-download-btn w-100"
						>
							<i className="fe fe-download me-2"></i>Download Certificate
						</a>
					) : (
						<div className="mc-pending">
							<i className="fe fe-clock me-2"></i>
							Certificate not yet issued
						</div>
					)}
				</div>
			</Card.Body>
		</Card>
	);
};

const MyCertificates = () => {
	const { data, loading, refetch } = useQuery(GET_MY_EVENT_CERTIFICATES, {
		fetchPolicy: 'network-only',
	});

	const certificates = useMemo(
		() => data?.myEventCertificates || [],
		[data]
	);

	return (
		<ProfileLayout>
			<div className="mc-page">
				<div className="mc-page-header mb-4">
					<h3 className="mc-page-title">My Certificates</h3>
					<p className="mc-page-sub text-muted">
						E-certificates of attendance for events you have participated in.
					</p>
				</div>

				{loading && (
					<div className="mc-loading">
						<Spinner animation="border" size="sm" className="me-2" />
						Loading certificates…
					</div>
				)}

				{!loading && certificates.length === 0 && (
					<div className="mc-empty">
						<i className="fe fe-award mc-empty-icon"></i>
						<h5 className="mc-empty-title">No certificates yet</h5>
						<p className="text-muted mb-3">
							If you recently submitted event feedback, your certificate may still be generating.
						</p>
						<button className="btn mc-browse-btn mb-3" onClick={() => refetch()}>
							<i className="fe fe-refresh-cw me-2"></i>Check Again
						</button>
						<Link to={PATH_EVENTS.root} className="btn mc-browse-btn" style={{ background: 'none', border: '1.5px solid #e2e8f0', color: '#64748b' }}>
							<i className="fe fe-calendar me-2"></i>Browse Events
						</Link>
					</div>
				)}

				{!loading && certificates.length > 0 && (
					<>
						<p className="mc-count mb-4">
							{certificates.length} certificate{certificates.length !== 1 ? 's' : ''}
						</p>
						<Row className="g-4">
							{certificates.map((cert) => (
								<Col key={cert.guid} xs={12} md={6} lg={4}>
									<CertificateCard cert={cert} />
								</Col>
							))}
						</Row>
					</>
				)}
			</div>

			<style>{`
				.mc-page-title { font-size: 1.375rem; font-weight: 800; color: #1e293b; margin-bottom: 0.25rem; }
				.mc-page-sub { font-size: 0.9375rem; }
				.mc-count { font-size: 0.875rem; color: #94a3b8; font-weight: 600; }
				.mc-loading {
					display: flex; align-items: center; justify-content: center;
					padding: 3rem; color: #94a3b8; font-size: 0.875rem;
				}
				.mc-empty {
					display: flex; flex-direction: column; align-items: center;
					justify-content: center; padding: 4rem 2rem; text-align: center;
				}
				.mc-empty-icon { font-size: 3rem; color: #e2e8f0; margin-bottom: 1rem; }
				.mc-empty-title { font-size: 1.125rem; font-weight: 700; color: #1e293b; }
				.mc-browse-btn {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					color: white; border: none; border-radius: 10px;
					padding: 0.625rem 1.5rem; font-weight: 600; font-size: 0.9375rem;
					display: inline-flex; align-items: center;
				}
				.mc-browse-btn:hover { opacity: 0.9; color: white; }

				/* Card */
				.mc-card { border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.04); transition: box-shadow 0.2s; }
				.mc-card:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.08); }
				.mc-card-bar { height: 4px; }
				.mc-type-badge { font-size: 0.7rem; font-weight: 700; padding: 4px 10px; border-radius: 8px; text-transform: uppercase; letter-spacing: 0.4px; }
				.mc-attended-badge { background: rgba(16,185,129,0.1) !important; color: #10b981 !important; font-size: 0.7rem; font-weight: 700; padding: 4px 10px; border-radius: 8px; }
				.mc-event-title { font-size: 1rem; font-weight: 700; color: #1e293b; line-height: 1.4; }
				.mc-speaker { font-size: 0.8125rem; color: #64748b; margin: 0; display: flex; align-items: center; }
				.mc-meta { display: flex; flex-direction: column; gap: 0.4rem; }
				.mc-meta-row { font-size: 0.8125rem; color: #64748b; display: flex; align-items: center; }
				.mc-download-btn {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					color: white; border: none; border-radius: 10px; padding: 0.625rem;
					font-weight: 600; font-size: 0.875rem; text-decoration: none;
					display: flex; align-items: center; justify-content: center; transition: opacity 0.2s;
				}
				.mc-download-btn:hover { opacity: 0.9; color: white; }
				.mc-pending {
					background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 10px;
					padding: 0.625rem; font-size: 0.8125rem; color: #94a3b8;
					display: flex; align-items: center; justify-content: center;
				}
			`}</style>
		</ProfileLayout>
	);
};

export default MyCertificates;
